import { Button, Dialog, DialogButtons, Spinner } from '@st/theme'
import { setToggle } from '@st/util/array-set'
import { formatFileSize } from '@st/util/file'
import { humanJoin } from '@st/util/inflect'
import { useMemo, useState } from 'react'
import { match } from '@st/pattern'
import { type BulkUploadDialogState } from '../st-folder-types'

export function BulkUploadDialog({
  entries,
  onUpload,
  onClose
}: {
  entries: BulkUploadDialogState
  onUpload: (selectedFilePaths: string[]) => void
  onClose: () => void
}) {
  const [selection, setSelection] = useState<
    { status: 'pristine' } | { status: 'edited'; selectedFilePaths: string[] }
  >({ status: 'pristine' })
  const [submitting, setSubmitting] = useState(false)

  const selectedFilePaths = useMemo(() => {
    if (entries.status != 'resolved') {
      return []
    }
    if (selection.status == 'pristine') {
      return entries.entries.uniqueFiles.map((entry) => entry.path)
    }
    return selection.selectedFilePaths
  }, [entries, selection])

  return (
    <Dialog
      className="w-full max-w-3xl"
      title={`Upload documents from ${entries.source}`}
      buttons={
        <DialogButtons>
          <Button variant="default" disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={submitting || selectedFilePaths.length == 0}
            onClick={() => {
              setSubmitting(true)
              onUpload(selectedFilePaths)
            }}
          >
            Upload
          </Button>
        </DialogButtons>
      }
    >
      {match(entries)
        .with({ status: 'resolving' }, () => (
          <div className="flex h-full w-full items-center justify-center">
            <Spinner className="size-12 fill-blue-600 text-gray-200" />
          </div>
        ))
        .with({ status: 'resolved' }, ({ entries }) => (
          <table className="w-full table-fixed divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="w-8"></th>
                <th className="px-2 py-2 text-left text-xs font-normal uppercase text-gray-500">
                  File name
                </th>
                <th className="w-32 px-2 py-2 text-right text-xs font-normal uppercase text-gray-500">
                  Size
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entries.uniqueFiles.map((entry) => (
                <tr key={entry.path}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="h-3 w-3"
                      disabled={submitting}
                      checked={selectedFilePaths.includes(entry.path)}
                      onChange={() => {
                        setSelection({
                          status: 'edited',
                          selectedFilePaths: setToggle(selectedFilePaths, entry.path)
                        })
                      }}
                    />
                  </td>
                  <td className="px-2 py-2 text-base text-gray-900">
                    <div className="overflow-hidden text-ellipsis text-nowrap">{entry.path}</div>
                  </td>
                  <td className="px-2 py-2 text-right text-base text-gray-700">
                    {formatFileSize(entry.size)}
                  </td>
                  <td className="py-2 text-base text-gray-700">New file</td>
                </tr>
              ))}

              {entries.duplicates.map((entry) => (
                <tr key={entry.path}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="h-3 w-3"
                      disabled={submitting}
                      checked={selectedFilePaths.includes(entry.path)}
                      onChange={() => {
                        setSelection({
                          status: 'edited',
                          selectedFilePaths: setToggle(selectedFilePaths, entry.path)
                        })
                      }}
                    />
                  </td>
                  <td className="px-2 py-2 text-base text-gray-500">
                    <div className="overflow-hidden text-ellipsis text-nowrap">{entry.path}</div>
                  </td>
                  <td className="px-2 py-2 text-right text-base text-gray-500">
                    {formatFileSize(entry.size)}
                  </td>
                  <td className="py-2 text-base text-gray-500">
                    Duplicate of{' '}
                    {humanJoin(
                      entry.duplicates.map((document) => document.name),
                      'and'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))
        .with({ status: 'failed' }, () => (
          <div>An error occurred while reading the selected files.</div>
        ))
        .exhaustive()}
    </Dialog>
  )
}
